/**
 * The sticky sub-tab strip under a section's breadcrumb — shared by AdminLayout
 * (Pricing's tabs) and SizersLayout (the sizer tabs).
 *
 * It pins to the top of the section's scroll column (the layout's main column),
 * so the switcher stays reachable down a long page while the breadcrumb above
 * scrolls away.
 *
 * `variant`:
 *   "admin" (default) — the Admin section's strip.
 *   "page"            — the Sizers strip: 14px labels, slate-500 at rest,
 *                       12px × 10px tabs, and a 3px indicator in the SAME
 *                       teal-700 as the active label. The strip's 16px inset
 *                       plus the tab's 10px puts the first label on the
 *                       breadcrumb's 26px left edge.
 */
import Box from "@mui/material/Box";
import { useNavigate } from "react-router-dom";

import { slate } from "../theme/colors";
import { A } from "../screens/admin/shared/adminTokens";

const LOOKS = {
  admin: {
    strip: { gap: 0.25, px: 1 },
    tab: (on) => ({
      fontSize: 13,
      fontWeight: on ? 600 : 500,
      px: 2,
      pt: 1.25,
      pb: 1.125,
      color: on ? A.tealDeep : A.muted,
      borderBottom: `2px solid ${on ? A.teal : "transparent"}`,
    }),
  },
  page: {
    strip: { gap: 0, px: 2 },
    tab: (on) => ({
      fontSize: 14,
      fontWeight: on ? 600 : 500,
      padding: "12px 10px",
      // Fixed line box so the strip is exactly SIZER_TABS_H (47px) tall — the
      // scenario workspace header pins directly under it.
      lineHeight: "20px",
      color: on ? A.tealDeep : slate[500],
      borderBottom: `3px solid ${on ? A.tealDeep : "transparent"}`,
    }),
  },
};

export default function SectionSubTabs({ tabs, activeTo, variant = "admin" }) {
  const navigate = useNavigate();
  if (!tabs?.length) return null;
  const look = LOOKS[variant] || LOOKS.admin;

  return (
    <Box
      sx={{
        position: "sticky",
        zIndex: 80,
        top: 0,
        display: "flex",
        bgcolor: A.surface,
        borderBottom: `1px solid ${A.line}`,
        overflowX: "auto",
        "&::-webkit-scrollbar": { height: 0 },
        ...look.strip,
      }}
    >
      {tabs.map((s) => {
        const on = s.to === activeTo;
        return (
          <Box
            key={s.to}
            component="button"
            type="button"
            aria-current={on ? "page" : undefined}
            onClick={() => navigate(s.to)}
            sx={{
              border: "none",
              background: "none",
              cursor: "pointer",
              font: "inherit",
              whiteSpace: "nowrap",
              mb: "-1px",
              transition: "color .13s",
              ...look.tab(on),
              "&:hover": { color: on ? A.tealDeep : A.ink },
            }}
          >
            {s.label}
          </Box>
        );
      })}
    </Box>
  );
}
